import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import ItemService from "../../../../services/customer_appCommonService";
import { InputField } from "../../../../Components/formElements/InputField";
import { ROLES, FINANCE_STATUS } from "./ServiceConstants";
import { hasRole } from "./serviceHelpers";

// ── Constants ─────────────────────────────────────────────────────────────────
const PAYMENT_MODES = [
  "Cash",
  "UPI",
  "Card",
  "Cheque",
  "Online",
  "Paytm",
  "Bank Transfer",
  "Card&Cash",
  "Cheque&Cash",
];

const tableHeadStyle = {
  headRow: { style: { backgroundColor: "#ebf0f5", fontWeight: "bold" } },
};

// ── Date formatter (DD-MM-YYYY) ───────────────────────────────────────────────
const fmtDate = (date) => {
  if (!date) return "";
  const [year, month, day] = String(date).split("T")[0].split("-");
  return day && month && year ? `${day}-${month}-${year}` : date;
};

// ── Reusable cell components (same pattern as ERFPaymentAdjustmentDetails) ────
const ReadCell = ({ value }) => (
  <div className="m-1">
    <InputField className="inputBox" value={value ?? ""} disabled />
  </div>
);

const EditCell = ({ value, type = "text", onChange }) => (
  <div className="m-1">
    <InputField
      className="inputBox"
      type={type}
      value={value ?? ""}
      onChange={onChange}
    />
  </div>
);

const SelectCell = ({ value, options, onChange }) => (
  <select
    className="form-select form-select-sm inputBox"
    value={value ?? ""}
    onChange={onChange}
  >
    <option value="">Select</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

// Shows who confirmed + when — renders under the status badge for saved rows
const ConfirmDetailCell = ({ confirmation }) => {
  if (!confirmation?.confirmed) return <span className="text-muted">—</span>;

  const dt = new Date(confirmation.confirmedAt);
  const date = dt.toLocaleDateString("en-GB");
  const time = dt.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="p-2">
      <div>{date}</div>
      <div className="text-muted" style={{ fontSize: 11 }}>
        {time}
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>
        {confirmation.confirmedByName || confirmation.confirmedBy}
      </div>
      <div style={{ fontSize: 11, color: "#2d7a79", fontWeight: "500" }}>
        {confirmation?.remarks}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const ServicePaymentSection = ({ serviceRecord, onRefresh }) => {
  const userData = useSelector((state) => state?.user);
  const roles = userData?.data?.roles || [];

  const isSE = hasRole(roles, ROLES.SERVICE_EXECUTIVE);
  const isFinance = hasRole(roles, ROLES.FI_EXECUTIVE);

  const currentUser = {
    userId: userData?.data?.userId || "",
    name:
      userData?.data?.firstName ||
      userData?.data?.name ||
      userData?.data?.userId ||
      "",
  };

  const [loading, setLoading] = useState(false);
  const [collectionStatus, setCollectionStatus] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    rowIndex: null,
    remarks: "",
  });

  // ── Split state ───────────────────────────────────────────────────────────
  // paymentHistory  → rows that are already saved to DB   (no local_id → read-only in table)
  // newRows         → rows added this session             (has local_id  → editable in table)
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [newRows, setNewRows] = useState([]);

  // ── Load saved rows from serviceRecord ────────────────────────────────────
  // Strip any stale local_id so they always render as read-only.
  // Normalise old shape (collectedAmount) → new shape (amount) for backward compat.
  useEffect(() => {
    if (!serviceRecord) return;

    setCollectionStatus(serviceRecord.financeCollectionConfirmation ?? null);

    const saved = (serviceRecord.paymentCollections || []).map(
      // eslint-disable-next-line no-unused-vars
      ({ local_id, ...rest }) => {
        // Backward compat: old records used collectedAmount
        if (rest.collectedAmount !== undefined && rest.amount === undefined) {
          return { ...rest, amount: rest.collectedAmount };
        }
        return rest;
      },
    );

    setPaymentHistory(saved);
    setNewRows([]); // always start fresh so stale edits don't leak across refreshes
  }, [serviceRecord]);

  // ── Combined view: saved first (read-only), then new (editable) ───────────
  const allRows = [...paymentHistory, ...newRows];

  // ── Summary calculations ──────────────────────────────────────────────────
  const totalToCollect = Number(serviceRecord?.amountToCollect || 0);
  const totalCollected = allRows.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );
  const balance = totalToCollect - totalCollected;

  // ── 1. SE adds a new editable row ─────────────────────────────────────────
  const handleAddRow = () => {
    const newRow = {
      local_id: `pay-${Date.now()}`,
      paymentDate: new Date().toISOString().split("T")[0],
      paymentMode: "",
      amount: "",
      chequeNo: "NA",
      cardNo: "NA",
      bank: "NA",
      notes: "",
      collectedBy: currentUser.userId,
      collectedByName: currentUser.name,
      collectedAt: new Date().toISOString(),
      seConfirmation: {
        confirmed: false,
        confirmedBy: null,
        confirmedByName: null,
        confirmedAt: null,
      },
      financeConfirmation: {
        confirmed: false,
        confirmedBy: null,
        confirmedByName: null,
        confirmedAt: null,
      },
    };
    setNewRows((prev) => [...prev, newRow]);
  };

  // ── 2. Update a field inside a new (unsaved) row ──────────────────────────
  const handleRowChange = (local_id, field, value) => {
    setNewRows((prev) =>
      prev.map((r) => (r.local_id === local_id ? { ...r, [field]: value } : r)),
    );
  };

  // ── 3. Remove a new (unsaved) row ─────────────────────────────────────────
  const handleRemoveRow = (local_id) => {
    setNewRows((prev) => prev.filter((r) => r.local_id !== local_id));
  };

  // ── 4. SE confirms their new row inline before saving ────────────────────
  // Validation: amount + paymentMode must be filled first
  const handleSeConfirm = (local_id) => {
    const row = newRows.find((r) => r.local_id === local_id);
    if (!row) return;

    if (!row.amount || Number(row.amount) <= 0) {
      return toast.error("Enter a valid amount before confirming");
    }
    if (!row.paymentMode) {
      return toast.error("Select a payment mode before confirming");
    }

    setNewRows((prev) =>
      prev.map((r) =>
        r.local_id === local_id
          ? {
              ...r,
              seConfirmation: {
                confirmed: true,
                confirmedBy: currentUser.userId,
                confirmedByName: currentUser.name,
                confirmedAt: new Date().toISOString(),
              },
            }
          : r,
      ),
    );
  };

  // ── 5. Save — only newRows are merged into history and sent to DB ─────────
  const handleSave = async () => {
    if (newRows.length === 0) {
      return toast.error("No new rows to save");
    }

    const unconfirmed = newRows.filter((r) => !r.seConfirmation?.confirmed);
    if (unconfirmed.length > 0) {
      return toast.error(`Confirm ${unconfirmed.length} row(s) before saving`);
    }

    setLoading(true);
    try {
      const merged = [...paymentHistory, ...newRows];
      const totalCollectedMerged = merged.reduce(
        (s, r) => s + (Number(r.amount) || 0),
        0,
      );

      await ItemService.updateErfService({
        erfServiceNumber: serviceRecord.erfServiceNumber,
        paymentCollections: merged,
        totalCollected: totalCollectedMerged,
        financeStatus: FINANCE_STATUS.PENDING,
      });

      toast.success("Payment saved");
      onRefresh?.();
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save payment");
    } finally {
      setLoading(false);
    }
  };

  // ── 6. Finance confirms a saved row ───────────────────────────────────────
  // rowIndex here is the absolute index in allRows. Since allRows = [...paymentHistory, ...newRows]
  // and finance can only confirm saved rows (no local_id), rowIndex === paymentHistory index. ✓
  const handleFinanceConfirm = async (rowIndex, remarks) => {
    setLoading(true);
    try {
      const now = new Date().toISOString();

      const updatedHistory = paymentHistory.map((r, i) =>
        i === rowIndex
          ? {
              ...r,
              financeConfirmation: {
                confirmed: true,
                confirmedBy: currentUser.userId,
                confirmedByName: currentUser.name,
                confirmedAt: now,
                remarks,
              },
            }
          : r,
      );

      const allConfirmed = updatedHistory.every(
        (r) => r.financeConfirmation?.confirmed,
      );

      await ItemService.updateErfService({
        erfServiceNumber: serviceRecord.erfServiceNumber,
        paymentCollections: updatedHistory,
        financeStatus: allConfirmed
          ? FINANCE_STATUS.CONFIRMED
          : FINANCE_STATUS.PENDING,
      });

      toast.success("Payment confirmed by Finance");
      onRefresh?.();
    } catch (err) {
      console.error("Finance confirm error:", err);
      toast.error("Failed to confirm payment");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCollection = async () => {
    setLoading(true);
    try {
      await ItemService.updateErfService({
        erfServiceNumber: serviceRecord.erfServiceNumber,
        financeCollectionConfirmation: "Confirmed",
      });
      setCollectionStatus("Confirmed");
      toast.success("Collection confirmed by Finance");
      onRefresh?.();
    } catch (err) {
      console.error("Finance confirm collection error:", err);
      toast.error("Failed to confirm collection");
    } finally {
      setLoading(false);
    }
  };

  // ── Column definitions ────────────────────────────────────────────────────
  const columns = [
    {
      name: "Payment Date",
      width: "175px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            type="date"
            value={row.paymentDate}
            onChange={(e) =>
              handleRowChange(row.local_id, "paymentDate", e.target.value)
            }
          />
        ) : (
          <ReadCell value={fmtDate(row.paymentDate)} />
        ),
    },
    {
      name: "Payment Mode",
      width: "165px",
      cell: (row) =>
        row.local_id ? (
          <SelectCell
            value={row.paymentMode}
            options={PAYMENT_MODES}
            onChange={(e) =>
              handleRowChange(row.local_id, "paymentMode", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.paymentMode} />
        ),
    },
    {
      name: "Amount (₹)",
      width: "155px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            type="number"
            value={row.amount}
            onChange={(e) =>
              handleRowChange(row.local_id, "amount", e.target.value)
            }
          />
        ) : (
          // Read-only amount with green colour to match existing style
          <div className="m-1">
            <InputField
              className="inputBox"
              value={`₹ ${Number(row.amount || 0).toFixed(2)}`}
              disabled
            />
          </div>
        ),
    },
    {
      name: "Cheque No.",
      width: "155px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.chequeNo}
            onChange={(e) =>
              handleRowChange(row.local_id, "chequeNo", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.chequeNo ?? "NA"} />
        ),
    },
    {
      name: "Card No.",
      width: "155px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.cardNo}
            onChange={(e) =>
              handleRowChange(row.local_id, "cardNo", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.cardNo ?? "NA"} />
        ),
    },
    {
      name: "Bank",
      width: "155px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.bank}
            onChange={(e) =>
              handleRowChange(row.local_id, "bank", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.bank ?? "NA"} />
        ),
    },
    {
      name: "Notes",
      width: "185px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.notes}
            onChange={(e) =>
              handleRowChange(row.local_id, "notes", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.notes || "--"} />
        ),
    },
    {
      name: "Collected By",
      width: "150px",
      cell: (row) => (
        <ReadCell value={row.collectedByName || row.collectedBy || "--"} />
      ),
    },
    {
      // ── Status column: drives the whole SE → Finance confirmation flow ─────
      // New row  (has local_id)  → SE Confirm button / SE Confirmed badge
      // Saved row (no local_id)  → Finance Approve button / Approved badge / Pending badge
      name: "Status",
      width: "185px",
      cell: (row, rowIndex) => {
        if (row.local_id) {
          // ── New unsaved row ──
          if (row.seConfirmation?.confirmed) {
            return (
              <span className="badge bg-success">
                <i className="bi bi-check-circle me-1" />
                SE Confirmed
              </span>
            );
          }
          return (
            <button
              className="btn btn-sm btn-outline-warning"
              onClick={() => handleSeConfirm(row.local_id)}
            >
              <i className="bi bi-check-circle me-1" />
              SE Confirm
            </button>
          );
        }

        // ── Saved row ──
        // rowIndex in allRows === paymentHistory index for rows without local_id
        // because allRows = [...paymentHistory, ...newRows] and paymentHistory rows are first
        if (row.financeConfirmation?.confirmed) {
          return (
            <span className="badge bg-success">
              <i className="bi bi-check-circle me-1" />✓ Approved
            </span>
          );
        }
        if (isFinance) {
          return (
            <button
              className="btn btn-sm btn-outline-success"
              disabled={loading}
              onClick={() =>
                setConfirmModal({ show: true, rowIndex, remarks: "" })
              }
            >
              <i className="bi bi-check-circle me-1" />
              Finance Approve
            </button>
          );
        }
        return <span className="badge bg-info text-dark">Pending Finance</span>;
      },
    },
    {
      name: "SE Confirmation",
      width: "175px",
      cell: (row) => <ConfirmDetailCell confirmation={row.seConfirmation} />,
    },
    {
      name: "Finance Confirmation",
      width: "175px",
      cell: (row) => (
        <ConfirmDetailCell confirmation={row.financeConfirmation} />
      ),
    },
    {
      name: "Action",
      width: "80px",
      cell: (row) =>
        row.local_id ? (
          <button
            className="btn btn-danger btn-sm"
            title="Remove row"
            onClick={() => handleRemoveRow(row.local_id)}
          >
            <i className="bi bi-trash3" />
          </button>
        ) : null,
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-2">
      {/* ── Summary bar ────────────────────────────────────────────────────── */}
      <Row className="mb-3 pt-2 border-bottom pb-3">
        <Col xs={12} lg={4} className="commTopButtonRightLeftPadding mb-1">
          <span className="fw-medium">
            To Collect: <strong>₹ {totalToCollect.toFixed(2)}</strong>
          </span>
        </Col>
        <Col xs={12} lg={4} className="commTopButtonRightLeftPadding mb-1">
          <span className="fw-medium">
            Collected:{" "}
            <strong style={{ color: "green" }}>
              ₹ {totalCollected.toFixed(2)}
            </strong>
          </span>
        </Col>
        <Col xs={12} lg={4} className="commTopButtonRightLeftPadding mb-1">
          <span className="fw-medium">
            Balance:{" "}
            <strong style={{ color: balance > 0 ? "red" : "green" }}>
              ₹ {balance.toFixed(2)}
            </strong>
          </span>
        </Col>
      </Row>

      {/* ── Table header row with "Add Row" button ──────────────────────────── */}
      <Row className="bg-light p-2 border rounded-top align-items-center mb-0">
        <Col xs={8} md={10} className="commTopButtonRightLeftPadding">
          <h6 className="fw-bold mb-0 text-primary ps-2">
            Payment Collections
          </h6>
        </Col>
        {isSE && (
          <Col xs={4} md={2} className="commTopButtonRightLeftPadding">
            <button className="btnTable" onClick={handleAddRow}>
              <i className="bi bi-plus-lg me-1" />
              Add Row
            </button>
          </Col>
        )}
      </Row>

      {/* ── DataTable ──────────────────────────────────────────────────────── */}
      <DataTable
        columns={columns}
        data={allRows}
        customStyles={tableHeadStyle}
        noDataComponent={
          <div className="p-4 text-center text-muted">
            <i className="bi bi-cash-stack fs-3 d-block mb-2" />
            No collections yet
          </div>
        }
      />

      {/* ── Save button — only appears when there are unsaved new rows ─────── */}
      {newRows.length > 0 && (
        <div className="mt-3 d-flex justify-content-between align-items-center px-1">
          <span className="text-muted small">
            {newRows.filter((r) => r.seConfirmation?.confirmed).length}/
            {newRows.length} row(s) confirmed
          </span>
          <Col xs={8} md={4} lg={3} className="commTopButtonRightLeftPadding">
            <button
              className="btnTable"
              disabled={loading}
              onClick={handleSave}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <i className="bi bi-save" />
              {loading ? "Saving..." : "Save Payment"}
            </button>
          </Col>
        </div>
      )}

      {/* ── Finance Collection Confirmation (visible when any payment exists) ── */}
      {paymentHistory.length > 0 && (
        <Row className="align-items-center mx-1 mt-5 mb-3 p-2 border rounded bg-light justify-content-between">
          <Col xs={6} md={3} className="pb-2 ps-2">
            <strong className="me-2">Finance Collection:</strong>
            {collectionStatus === "Confirmed" ? (
              <span className="badge bg-success">&#10003; Confirmed</span>
            ) : (
              <span className="badge bg-warning text-dark">Not Confirmed</span>
            )}
          </Col>
          {isFinance && collectionStatus !== "Confirmed" && (
            <Col xs={6} md={2} className="pb-2 ps-2">
              <button
                className="btnTable w-100"
                onClick={handleConfirmCollection}
                disabled={loading}
              >
                Confirm Collection
              </button>
            </Col>
          )}
        </Row>
      )}

      {/* ── Finance Row Approval Modal ─────────────────────────────────────── */}
      <Modal
        show={confirmModal.show}
        onHide={() =>
          setConfirmModal({ show: false, rowIndex: null, remarks: "" })
        }
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Finance Approval</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-semibold">
              Remarks <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Enter approval remarks..."
              value={confirmModal.remarks}
              onChange={(e) =>
                setConfirmModal((prev) => ({
                  ...prev,
                  remarks: e.target.value,
                }))
              }
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btnTable btn-danger"
            onClick={() =>
              setConfirmModal({ show: false, rowIndex: null, remarks: "" })
            }
          >
            Cancel
          </button>
          <button
            className="btnTable"
            disabled={loading || !confirmModal.remarks.trim()}
            onClick={async () => {
              await handleFinanceConfirm(
                confirmModal.rowIndex,
                confirmModal.remarks.trim(),
              );
              setConfirmModal({ show: false, rowIndex: null, remarks: "" });
            }}
          >
            {loading ? "Saving..." : "Approve"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ServicePaymentSection;
